import { Injectable } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { BaseEntityLayerManager } from '../core/base/base-entity-layer-manager';
import { IStatefulEntity } from '../core/interfaces/entity-layer-manager.interface';
import {
  EstadoEmergenciaMedica,
  IEmergencia,
  PrioridadEmergenciaMedica,
} from 'modelos/src';

/**
 * Estados de emergencias médicas
 */

/**
 * EmergenciasMedicasLayerManager
 *
 * Manager para renderizar emergencias médicas en el mapa.
 *
 * Características:
 * - Estados dinámicos (7 estados diferentes)
 * - Iconos según estado
 * - NO soporta clustering (emergencias son pocas y críticas)
 * - Implementa IStatefulEntity
 */
@Injectable({
  providedIn: 'root',
})
export class EmergenciasMedicasLayerManager
  extends BaseEntityLayerManager<IEmergencia>
  implements IStatefulEntity<EstadoEmergenciaMedica>
{
  // ========== CONFIGURACIÓN ==========
  readonly entityType = 'emergenciasMedicas';
  readonly supportsClustering = false;
  readonly clusterColor: string | undefined = undefined; // Emergencias críticas, no agrupar

  // IDs de layers
  private mainLayerId = 'emergencias-medicas-layer';
  private circuloLayerId = 'circulo-emergencia-layer';
  private circuloSourceId = 'circulo-emergencia-source';

  // Modo de visualización
  private modo: 'estado' | 'prioridad' = 'prioridad';

  // ========== MAPEO DE ESTADOS ==========
  // Los pins (assets/pins/emergencias/emergencia_*.svg) están pintados con los
  // colores de la paleta única del front: modulos/entidades/emergencias/
  // estado-emergencia.ts (presentacionEstado / presentacionPrioridad). Si
  // cambia un color ahí, hay que cambiar el SVG correspondiente.

  readonly stateToIconMap: Partial<Record<EstadoEmergenciaMedica, string>> = {
    Pendiente: 'emergencia-pendiente-icon',
    Asignada: 'emergencia-asignada-icon',
    Reasignada: 'emergencia-reasignada-icon',
    'En tránsito': 'emergencia-transito-icon',
    'Llegó a destino': 'emergencia-destino-icon',
    'Rumbo a hospital': 'emergencia-hospital-icon',
    'Llegada a hospital': 'emergencia-llego-hospital-icon',
    Finalizada: 'emergencia-finalizada-icon',
  };

  // Mapeo de prioridades a iconos
  readonly prioridadToIconMap: Partial<
    Record<PrioridadEmergenciaMedica, string>
  > = {
    Rojo: 'emergencia-prioridad-roja-icon',
    Amarillo: 'emergencia-prioridad-amarilla-icon',
    Verde: 'emergencia-prioridad-verde-icon',
    Negro: 'emergencia-prioridad-negra-icon',
    Celeste: 'emergencia-prioridad-celeste-icon',
  };

  // ========== CONFIGURACIÓN DE MODO ==========

  /**
   * Configura el modo de visualización (estado vs prioridad)
   */
  setModo(modo: 'estado' | 'prioridad'): void {
    this.modo = modo;
    this.updateLayerExpressions();
  }

  /**
   * Obtiene el modo actual
   */
  getModo(): 'estado' | 'prioridad' {
    return this.modo;
  }

  /**
   * Actualiza las expresiones de iconos cuando cambia el modo
   */
  private updateLayerExpressions(): void {
    if (!this.map || !this.map.getLayer(this.mainLayerId)) return;

    const expression = this.getIconExpression();
    this.map.setLayoutProperty(this.mainLayerId, 'icon-image', expression);
  }

  // ========== RENDERIZACIÓN ==========

  override entityToFeature(
    emergencia: IEmergencia,
  ): GeoJSON.Feature<GeoJSON.Point> {
    // OPTIMIZACIÓN: Almacenar en mapa en vez de serializar
    this.storeEntity(emergencia);

    const estado = this.determineEntityState(emergencia);
    const prioridad = this.getPrioridad(emergencia);

    let coordinates: [number, number];
    if (
      emergencia.ubicacionDestino?.coordinates &&
      emergencia.ubicacionDestino?.coordinates[0] !== undefined &&
      emergencia.ubicacionDestino?.coordinates[1] !== undefined
    ) {
      coordinates = emergencia.ubicacionDestino?.coordinates;
    } else {
      console.warn(`⚠️ Emergencia ${emergencia._id} sin coordenadas válidas`);
      coordinates = [-58.443832, -34.587386];
    }

    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates,
      },
      properties: {
        id: emergencia._id,
        estado: estado,
        prioridad: prioridad,
        direccion: emergencia.direccion,
        observaciones: emergencia.observaciones,
      },
    };
  }

  determineEntityState(emergencia: IEmergencia): EstadoEmergenciaMedica {
    return (
      (emergencia.ultimoEventoEmergencia?.estado as EstadoEmergenciaMedica) ||
      'Pendiente'
    );
  }

  /**
   * Obtiene la prioridad de una emergencia
   */
  getPrioridad(emergencia: IEmergencia): PrioridadEmergenciaMedica | '' {
    // Sin código cae en el ícono genérico (antes se pintaba como Verde)
    return (
      (emergencia.prioridadCierre as PrioridadEmergenciaMedica) ||
      (emergencia.prioridadApertura as PrioridadEmergenciaMedica) ||
      ''
    );
  }

  override getIconExpression(): mapboxgl.Expression {
    // Modo prioridad: usar prioridad
    if (this.modo === 'prioridad') {
      return [
        'match',
        ['get', 'prioridad'],
        'Rojo',
        this.prioridadToIconMap['Rojo'],
        'Amarillo',
        this.prioridadToIconMap['Amarillo'],
        'Verde',
        this.prioridadToIconMap['Verde'],
        'Negro',
        this.prioridadToIconMap['Negro'],
        'Celeste',
        this.prioridadToIconMap['Celeste'],
        'emergencia-default-icon', // Default
      ];
    }

    // Modo estado: usar estado (comportamiento original)
    return [
      'match',
      ['get', 'estado'],
      'Pendiente',
      this.stateToIconMap['Pendiente'],
      'Asignada',
      this.stateToIconMap['Asignada'],
      'Reasignada',
      this.stateToIconMap['Reasignada'],
      'En tránsito',
      this.stateToIconMap['En tránsito'],
      'Llegó a destino',
      this.stateToIconMap['Llegó a destino'],
      'Rumbo a hospital',
      this.stateToIconMap['Rumbo a hospital'],
      'Llegada a hospital',
      this.stateToIconMap['Llegada a hospital'],
      'emergencia-default-icon', // Default
    ];
  }

  // ========== CREACIÓN DE LAYERS ==========

  protected override createLayers(): void {
    if (!this.map) return;

    this.addLayer({
      id: this.mainLayerId,
      type: 'symbol',
      source: this.sourceId,
      layout: {
        'icon-image': this.getIconExpression(),
        'icon-size': 0.583, // SVG 60px → 35px (35/60 = 0.583)
        'icon-allow-overlap': true,
        'icon-padding': 0, // Cajas de colisión más pequeñas
      },
    });
  }

  // ========== VISIBILIDAD ==========

  override setVisibility(visible: boolean): void {
    if (!this.map) return;
    this.isVisible = visible;

    if (this.map.getLayer(this.mainLayerId)) {
      this.map.setLayoutProperty(
        this.mainLayerId,
        'visibility',
        visible ? 'visible' : 'none',
      );
    }
  }

  // ========== CÍRCULO DE RADIO ==========

  /**
   * Establece el mapa para poder dibujar el círculo
   * @param map Instancia del mapa de Mapbox
   */
  public setMapForCircle(map: mapboxgl.Map): void {
    this.map = map;
  }

  /**
   * Dibuja un círculo de radio alrededor de las coordenadas especificadas
   * @param coordenadas Coordenadas [lng, lat] del centro
   * @param radioMetros Radio del círculo en metros (default: 200)
   */
  public dibujarCirculoDeRadio(
    coordenadas: [number, number],
    radioMetros: number = 200,
  ): void {
    if (!this.map) {
      return;
    }

    // Limpiar círculo anterior si existe
    this.limpiarCirculoDeRadio();

    // Calcular el círculo usando turf o manualmente
    const circleGeoJSON = this.createCircleGeoJSON(coordenadas, radioMetros);

    try {
      // Agregar source
      this.map.addSource(this.circuloSourceId, {
        type: 'geojson',
        data: circleGeoJSON,
      });

      // Buscar la primera capa de símbolos (markers) para insertar el círculo debajo
      const layers = this.map.getStyle().layers;
      let firstSymbolId: string | undefined;
      if (layers) {
        for (const layer of layers) {
          if (layer.type === 'symbol') {
            firstSymbolId = layer.id;
            break;
          }
        }
      }

      // Agregar layer de relleno (semi-transparente) debajo de los markers
      this.map.addLayer(
        {
          id: `${this.circuloLayerId}-fill`,
          type: 'fill',
          source: this.circuloSourceId,
          paint: {
            'fill-color': '#ff0000',
            'fill-opacity': 0.2,
          },
        },
        firstSymbolId, // Insertar antes de la primera capa de símbolos
      );

      // Agregar layer de borde debajo de los markers
      this.map.addLayer(
        {
          id: this.circuloLayerId,
          type: 'line',
          source: this.circuloSourceId,
          paint: {
            'line-color': '#ff0000',
            'line-width': 1,
            'line-opacity': 0.8,
          },
        },
        firstSymbolId, // Insertar antes de la primera capa de símbolos
      );
    } catch (error) {
      console.error('❌ Error al agregar círculo al mapa:', error);
    }
  }

  /**
   * Limpia el círculo de radio del mapa
   */
  public limpiarCirculoDeRadio(): void {
    if (!this.map) return;

    // Remover layers
    if (this.map.getLayer(this.circuloLayerId)) {
      this.map.removeLayer(this.circuloLayerId);
    }
    if (this.map.getLayer(`${this.circuloLayerId}-fill`)) {
      this.map.removeLayer(`${this.circuloLayerId}-fill`);
    }

    // Remover source
    if (this.map.getSource(this.circuloSourceId)) {
      this.map.removeSource(this.circuloSourceId);
    }
  }

  /**
   * Crea un polígono circular en formato GeoJSON
   * @param center Coordenadas del centro [lng, lat]
   * @param radiusInMeters Radio en metros
   * @param points Número de puntos del polígono (default: 64)
   */
  private createCircleGeoJSON(
    center: [number, number],
    radiusInMeters: number,
    points: number = 64,
  ): GeoJSON.FeatureCollection {
    const coords = [];
    const distanceX =
      radiusInMeters / (111320 * Math.cos((center[1] * Math.PI) / 180));
    const distanceY = radiusInMeters / 110574;

    for (let i = 0; i < points; i++) {
      const theta = (i / points) * (2 * Math.PI);
      const x = distanceX * Math.cos(theta);
      const y = distanceY * Math.sin(theta);
      coords.push([center[0] + x, center[1] + y]);
    }
    coords.push(coords[0]); // Cerrar el polígono

    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [coords],
          },
          properties: {},
        },
      ],
    };
  }
}
